import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  ErrorInfo,
  nameRegExp,
  SectionHeader,
  TextFieldForm,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useParams } from 'react-router-dom';
import { Dayjs } from 'dayjs';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import ControlledAutocomplete from '../../../../../components/form-items/ControlledAutocomplete';
import { useCandidateDetailsForm } from '../../../logic/useCandidateDetailsForm';
import { IOptionType } from '../../../../../api/candidate/exam/model';
import { GetNonNationalCandidateDetailsResponse } from '../../../../../api-refactored/non-national-exam-registration/useGetNonNationalCandidateDetails';
import { useGetUserAuthorities } from '../../../../../api-refactored/user/useGetUserAuthorities';
import { existInArray } from '../../../../../utils/ArrayUtils';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  container: {
    marginTop: theme.spacing(-2),
  },
  titleStatement: {
    marginBottom: theme.spacing(4),
  },
  inputField: {
    marginBottom: theme.spacing(6),
  },
  inputSection: {
    marginBottom: theme.spacing(4),
  },
  sectionWrapper: {},
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  sectionSubTitleWrapper: {
    marginBottom: theme.spacing(4),
  },
  actionButton: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 'fit-content',
      marginLeft: theme.spacing(0),
      marginTop: theme.spacing(2),
      marginRight: theme.spacing(9),
    },
  },
  errMsg: {
    marginTop: '0 !important',
  },
  radioGroup: {
    marginBottom: theme.spacing(4),
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  additionalText: {
      fontSize: '0.875rem',
      marginTop: theme.spacing(1),
      lineHeight: 1.5,
      paddingLeft: theme.spacing(2),
  },
}));

interface SpersCandidateDetailsParams {
  uuid: string;
}

export interface SpersCandidateDetailsLocation {
  examType: string;
  examTitle: string;
  levelSeekingAdmission: IOptionType;
  isSupplementary: boolean;
}

interface SpersCandidateDetailsProps {
  isOpsAdmin?: boolean;
  citizenshipType: string;
  candidateDetailsData: GetNonNationalCandidateDetailsResponse | undefined;
  motherTongueLanguagesData: IOptionType[] | undefined;
}

const SpersCandidateDetails = ({
  isOpsAdmin,
  citizenshipType,
  candidateDetailsData,
  motherTongueLanguagesData,
}: SpersCandidateDetailsProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { classes } = useStyles();
  const { uuid } = useParams<SpersCandidateDetailsParams>();
  const [errorMsg, setErrorMsg] = useState<string>('');

  const {
    formHooks,
    nonNationalExamCreateCandidateStatus,
    submitContinue,
    nextPage,
  } = useCandidateDetailsForm({
    uuid,
    disableValidateSinBirthCertNo: citizenshipType !== 'SC',
    lockedByUser: candidateDetailsData?.lockedByUser,
    candidateDetailsData,
    motherTongueLanguagesData,
    setErrorMsg,
  });
  const { control, formState: { errors }, register, setValue, handleSubmit, watch } = formHooks;
  const { dateOfAttainedSPRStatus: dateOfAttainedSPRStatusWatch } = watch();
  const authorities = useGetUserAuthorities();
  const hasViewAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_VIEW)
    : true;
  const hasEditAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_EDIT)
    : true;
  const navigateBack = () => {
    history.goBack();
  };

  const apiButtonClick = () => {
    if (hasViewAccess && !hasEditAccess) {
      nextPage();
      return;
    }
    handleSubmit((formData) => {
      setErrorMsg('');
      submitContinue(formData);
    })();
  };

  return (
    <>
      <ContentTemplate isDividerBottom>
        <SectionHeader title={t('exam->>ParentParticulars.title')} />
        {!isOpsAdmin && (
          <Typography className={classes.titleStatement} variant="body1">
            {t('exam->>ParentParticulars.TitleStatement')}
          </Typography>
        )}
        <TextFieldForm
          id="name"
          className={classes.inputField}
          label={t('exam->>ParentParticulars.Name')}
          isRequired
          form={formHooks}
          name="name"
          type="text"
          error={!!errors?.name}
          helperText={errors?.name?.message?.toString()}
          disabled={!hasEditAccess}
          regExpToBlock={nameRegExp}
        />
        <TextFieldForm
          id="nricFin"
          className={classes.inputField}
          label={t('exam->>ParentParticulars.NricFin')}
          isRequired
          form={formHooks}
          name="uinFinPassport"
          type="text"
          onChange={(e) => formHooks.setValue('uinFinPassport', e.target.value.toUpperCase())}
          error={!!errors?.uinFinPassport}
          helperText={errors?.uinFinPassport?.message?.toString()}
          disabled={!hasEditAccess}
        />
      </ContentTemplate>

      <Box className={classes.container}>
        <ContentTemplate continuous>
          <Typography variant="h3" className={classes.sectionTitle}>
            {t('exam->>candidateDetails.title')}
          </Typography>

          {citizenshipType === 'SC' ? (
            <TextFieldForm
              id="birthCertNo"
              className={classes.inputSection}
              label={t('exam->>candidateDetails.SGBirthCertNo')}
              isRequired
              form={formHooks}
              name="singaporeBirthCertNo"
              type="text"
              error={!!errors?.singaporeBirthCertNo}
              helperText={errors?.singaporeBirthCertNo?.message?.toString()}
              disabled={!hasEditAccess}
            />
          ) : (
            <ControlledDatePicker
              className={classes.inputSection}
              label={t('exam->>candidateDetails.DateAttainedSPR')}
              onChange={(date) => {
                setValue('dateOfAttainedSPRStatus', date || '');
              }}
              error={!!errors.dateOfAttainedSPRStatus}
              helperText={errors.dateOfAttainedSPRStatus?.message?.toString()}
              inputVariant="filled"
              autoOk
              isRequired
              name="dateOfAttainedSPRStatus"
              disabled={!hasEditAccess}
              control={control}
            />
          )}

          <ControlledAutocomplete
            id="motherTongue"
            isRequired
            name="motherTongueLanguageToBeTaken"
            control={control}
            label={t('exam->>candidateDetails.motherTongue')}
            options={motherTongueLanguagesData || []}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            className={classes.inputSection}
            placeholder="Select mother tongue language"
            error={!!errors?.motherTongueLanguageToBeTaken}
            helperText={errors?.motherTongueLanguageToBeTaken?.message?.toString()}
            disableClearable
            readOnly
            disabled={!hasEditAccess}
            subLabel={
                <Typography variant="caption" className={classes.additionalText}>
                  For candidates selecting 'Chinese' as their Mother Tongue Language (MTL), please note the following:
                  <ul>
                    <li>If the candidate is eligible, opts for and is posted to a Special Assistance Plan (SAP) school, he/she may need to sit for a Higher Chinese Language or Chinese Language assessment to determine admission suitability.</li>
                    <li>Students in SAP schools must take Higher Chinese Language or Chinese Language at national exams and cannot seek MTL exemption.</li>
                  </ul>
                </Typography>
            }
            isSubLabelBelowLabel
          />

          {candidateDetailsData?.lockedByUser && (
            <Typography variant="body2" className={classes.errString}>
              {t('exam->>NonNationalExam.LockedMessage', {
                lockedByUser: candidateDetailsData?.lockedByUser,
              })}
            </Typography>
          )}

          <ErrorInfo className={classes.errString} label={errorMsg} />

          <Button
            id="button-back"
            type="button"
            variant="outlined"
            size="medium"
            color="secondary"
            onClick={navigateBack}
          >
            Back
          </Button>
          <ApiButton
            id="button-confirm-candidate-detail"
            className={classes.actionButton}
            status={nonNationalExamCreateCandidateStatus}
            color="secondary"
            size="medium"
            onClick={apiButtonClick}
          >
            Confirm Candidate Details
          </ApiButton>
        </ContentTemplate>
      </Box>
    </>
  );
};

export default SpersCandidateDetails;
